import type { SalesforcePaymentsSetupConfig } from '../../types/config';
import type { BillingDetails, PaymentSetupRequest } from '../../types/paymentRequest';
import type { SetupResponse } from '../../types/paymentResponse';
import type { PaymentMethodSet, PaymentMethod } from '../../types/paymentMethodSet';
import { ComponentType, PaymentMethodType } from '../../types/paymentMethodSet';
import type { SfpSavedPaymentMethodComponent } from '../../vendor/sfp-saved-payment-method-component';
import { PaymentSheet } from '../paymentSheet';
import { addClass } from '../../util/utility';
import type SfpContext from '../../sfp-context';
import type { MetadataReader } from '../../util/metadata-reader';

const SETUP_PAYMENT_METHODS: PaymentMethodType[] = [
  PaymentMethodType.AU_BECS_DEBIT,
  PaymentMethodType.BACS_DEBIT,
  PaymentMethodType.CARD,
  PaymentMethodType.SEPA_DEBIT,
  PaymentMethodType.US_BANK_ACCOUNT,
];

export class Setup extends PaymentSheet {
  TYPE = ComponentType.SETUP;

  config: SalesforcePaymentsSetupConfig;

  /**
   * @class Setup
   * @hideconstructor
   * @classdesc Payment sheet component to setup a saved payment method without making a payment at the time of setup. Presents a selection of payment methods determined by the payment method set and related configuration.
   * @extends Component
   * @public
   */
  constructor(
    context: SfpContext,
    metadataReader: MetadataReader,
    paymentMethodSet: PaymentMethodSet,
    config: SalesforcePaymentsSetupConfig,
    paymentSetupRequest: PaymentSetupRequest,
    elementOrSelectorToMountTo: string | HTMLElement
  ) {
    super(context, metadataReader, paymentMethodSet, config, paymentSetupRequest, elementOrSelectorToMountTo);
  }

  protected stylePaymentElement(): void {
    super.stylePaymentElement();
    addClass(this.paymentElement, 'sfpp-component-setup');
  }

  protected includePaymentMethod(paymentMethod: PaymentMethod): boolean {
    return SETUP_PAYMENT_METHODS.includes(paymentMethod.paymentMethodType) && super.includePaymentMethod(paymentMethod);
  }

  /**
   * Confirms saved payment method setup for the currently selected payment method using the values input by the user.
   * @param {BillingDetails} [billingDetails] - Billing information to associate with the saved payment method. If not provided here, the [PaymentSetupRequest.billingDetails]{@link PaymentSetupRequest} is used instead.
   * @returns {Promise<SetupResponse>} - Promise that resolves with a success/fail response to payment setup confirmation, or rejects if there was an error attempting confirmation.
   * @public
   */
  async confirm(billingDetails?: BillingDetails): Promise<SetupResponse> {
    this.paymentRequest.billingDetails = billingDetails;

    const savedPaymentMethodComp = this.selectedPaymentMethod as SfpSavedPaymentMethodComponent;
    return await savedPaymentMethodComp.handleConfirmSetup(this.config.actions.createSetupIntent, billingDetails);
  }
}
